import React, { useState, useEffect } from 'react';
import { 
  IconChartBar, 
  IconTrendingUp, 
  IconTrendingDown, 
  IconScale, 
  IconMoneybag, 
  IconCalculator,
  IconRefresh,
  IconArrowUpRight,
  IconCoins
} from '@tabler/icons-react';
import Layouts from '../../layouts/Layouts';
import { DinamicTitle } from '../../utils/DinamicTitle';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const GoldValuation = () => {
  DinamicTitle('Analitik Emas || Komaladewi');
  
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [simulatorAdjustment, setSimulatorAdjustment] = useState(0);
  const [benchmarkType, setBenchmarkType] = useState('week'); // 'today' or 'week'
  const [selectedKarat, setSelectedKarat] = useState(''); // Empty means 'Semua Karat'

  const karatOptions = [16, 17, 18, 19, 20, 21, 22, 23, 24];

  // Mock fetching data (This structure should be followed by your Laravel BE)
  useEffect(() => {
    setLoading(true);
    // Simulation: data changes slightly based on Karat
    const multiplier = selectedKarat ? (selectedKarat / 24) : 1;
    
    setTimeout(() => {
      setData({
        current_gold_price: Math.round(1245000 * multiplier),
        last_update: new Date().toLocaleString(),
        price_change_24h: Math.round(12500 * multiplier),
        price_change_percentage: 1.01,
        total_inventory_weight: selectedKarat ? 850.25 : 5230.75, 
        total_inventory_cost: Math.round(6125000000 * (selectedKarat ? 0.2 : 1)), 
        total_inventory_market_value: Math.round(6512283750 * (selectedKarat ? 0.2 : 1)),
        unrealized_profit: Math.round(387283750 * (selectedKarat ? 0.2 : 1)),
        avg_selling_price_today: Math.round(1315000 * multiplier),
        avg_selling_price_week: Math.round(1298000 * multiplier),
        history: [
          { date: '03 May', price: Math.round(1220000 * multiplier) },
          { date: '04 May', price: Math.round(1225000 * multiplier) },
          { date: '05 May', price: Math.round(1230000 * multiplier) },
          { date: '06 May', price: Math.round(1228000 * multiplier) },
          { date: '07 May', price: Math.round(1235000 * multiplier) },
          { date: '08 May', price: Math.round(1240000 * multiplier) },
          { date: '09 May', price: Math.round(1245000 * multiplier) },
        ]
      });
      setLoading(false);
    }, 800);
  }, [selectedKarat]);

  const numberWithCommas = (x) => {
    return x?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  };

  const chartData = {
    labels: data?.history.map(h => h.date) || [],
    datasets: [
      {
        label: 'Harga Emas (IDR/gr)',
        data: data?.history.map(h => h.price) || [],
        fill: true,
        borderColor: '#f59e0b',
        backgroundColor: 'rgba(245, 158, 11, 0.1)',
        tension: 0.4,
        pointRadius: 4,
        pointBackgroundColor: '#f59e0b',
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: function(context) {
            return `Rp ${numberWithCommas(context.parsed.y)}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: false,
        grid: { color: 'rgba(0,0,0,0.05)' },
        ticks: { font: { size: 10 } }
      },
      x: {
        grid: { display: false },
        ticks: { font: { size: 10 } }
      }
    }
  };

  const simulatedProfit = data ? (data.total_inventory_weight * simulatorAdjustment) : 0;

  if (loading) {
    return (
      <Layouts>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
        </div>
      </Layouts>
    );
  }

  return (
    <Layouts>
      <div className="p-4 space-y-6 animate-fade-in">
        
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div>
              <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
                <IconChartBar className="text-amber-500" />
                Golden Intelligence Analytics
              </h1>
              <p className="text-slate-500 dark:text-slate-400 text-sm">Update terakhir: {data.last_update}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="flex items-center bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-1.5 shadow-sm">
              <span className="text-xs font-bold text-slate-400 mr-2 uppercase">Filter:</span>
              <select 
                value={selectedKarat}
                onChange={(e) => setSelectedKarat(e.target.value)}
                className="bg-transparent text-sm font-bold text-slate-700 dark:text-slate-200 outline-none cursor-pointer"
              >
                <option value="">Semua Karat</option>
                {karatOptions.map(k => (
                  <option key={k} value={k}>{k} Karat</option>
                ))}
              </select>
            </div>
            
            <button 
              onClick={() => window.location.reload()}
              className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-all shadow-sm"
            >
              <IconRefresh size={18} />
            </button>
          </div>
        </div>

        {/* Top Cards Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          
          {/* Live Gold Price Card */}
          <div className="bg-gradient-to-br from-amber-400 to-amber-600 rounded-2xl p-5 text-white shadow-lg shadow-amber-500/20 relative overflow-hidden group">
            <div className="absolute -right-4 -top-4 opacity-10 group-hover:scale-110 transition-transform duration-500">
              <IconCoins size={120} />
            </div>
            <div className="relative z-10">
              <p className="text-amber-100 text-xs font-bold uppercase tracking-wider mb-1">
                Harga Spot {selectedKarat ? `${selectedKarat}K` : '24K'} (IDR/gr)
              </p>
              <h2 className="text-3xl font-black mb-2">Rp {numberWithCommas(data.current_gold_price)}</h2>
              <div className="flex items-center gap-1.5 text-xs font-bold bg-white/20 w-fit px-2 py-1 rounded-full">
                {data.price_change_24h > 0 ? <IconTrendingUp size={14} /> : <IconTrendingDown size={14} />}
                {data.price_change_percentage}% (Rp {numberWithCommas(data.price_change_24h)})
              </div>
            </div>
          </div>

          {/* Total Weight Card */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg">
                <IconScale size={20} />
              </div>
              <span className="text-slate-500 dark:text-slate-400 text-xs font-bold uppercase tracking-wider">Total Stok Emas</span>
            </div>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100">{data.total_inventory_weight.toFixed(2)} <span className="text-sm font-medium text-slate-400">Gram</span></h2>
            <p className="text-xs text-slate-400 mt-1">Estimasi seluruh stok di Brankas & Etalase</p>
          </div>

          {/* Inventory Market Value Card */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-lg">
                <IconMoneybag size={20} />
              </div>
              <span className="text-slate-500 dark:text-slate-400 text-xs font-bold uppercase tracking-wider">Nilai Pasar Stok</span>
            </div>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100">Rp {numberWithCommas(data.total_inventory_market_value)}</h2>
            <p className="text-xs text-emerald-500 font-bold mt-1 flex items-center gap-1">
              <IconArrowUpRight size={12} />
              Berdasarkan harga pasar hari ini
            </p>
          </div>

          {/* Unrealized Profit Card */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400 rounded-lg">
                <IconTrendingUp size={20} />
              </div>
              <span className="text-slate-500 dark:text-slate-400 text-xs font-bold uppercase tracking-wider">Equity vs Modal (Spot)</span>
            </div>
            <h2 className={`text-2xl font-bold ${data.unrealized_profit >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
              Rp {numberWithCommas(data.unrealized_profit)}
            </h2>
            <p className="text-xs text-slate-400 mt-1">Selisih Nilai Pasar (Spot) vs Modal</p>
          </div>

        </div>

        {/* Projected Profit Section (Based on Real Sales) */}
        <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl p-6 text-white shadow-xl relative overflow-hidden">
          <div className="absolute top-0 right-0 p-8 opacity-10">
            <IconMoneybag size={120} />
          </div>
          
          <div className="relative z-10">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
              <div>
                <h3 className="text-xl font-bold flex items-center gap-2">
                  <IconCoins className="text-amber-400" />
                  Proyeksi Keuntungan Realistis
                </h3>
                <p className="text-slate-400 text-sm mt-1">Estimasi profit berdasarkan rata-rata harga penjualan asli di toko</p>
              </div>
              
              <div className="flex bg-slate-700/50 p-1 rounded-xl border border-slate-600">
                <button 
                  onClick={() => setBenchmarkType('today')}
                  className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${benchmarkType === 'today' ? 'bg-amber-500 text-white shadow-lg' : 'text-slate-400 hover:text-slate-200'}`}
                >
                  Rata-rata Hari Ini
                </button>
                <button 
                  onClick={() => setBenchmarkType('week')}
                  className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${benchmarkType === 'week' ? 'bg-amber-500 text-white shadow-lg' : 'text-slate-400 hover:text-slate-200'}`}
                >
                  Rata-rata Minggu Ini
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="space-y-1">
                <p className="text-slate-400 text-[10px] font-bold uppercase tracking-widest">Harga Jual Acuan (ASP)</p>
                <h4 className="text-2xl font-black text-amber-400">
                  Rp {numberWithCommas(benchmarkType === 'today' ? data.avg_selling_price_today : data.avg_selling_price_week)} <span className="text-xs font-normal text-slate-400">/ gram</span>
                </h4>
                <p className="text-xs text-slate-500 italic">Diambil dari history transaksi {benchmarkType === 'today' ? 'hari ini' : '7 hari terakhir'}</p>
              </div>

              <div className="space-y-1">
                <p className="text-slate-400 text-[10px] font-bold uppercase tracking-widest">Potensi Omzet (Estimasi)</p>
                <h4 className="text-2xl font-black">
                  Rp {numberWithCommas(Math.round((benchmarkType === 'today' ? data.avg_selling_price_today : data.avg_selling_price_week) * data.total_inventory_weight))}
                </h4>
                <p className="text-xs text-slate-500">Jika seluruh stok ({data.total_inventory_weight} gr) terjual di harga acuan</p>
              </div>

              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4 border border-white/10">
                <p className="text-amber-200 text-[10px] font-bold uppercase tracking-widest">Estimasi Keuntungan Bersih</p>
                <h4 className="text-3xl font-black text-emerald-400">
                  Rp {numberWithCommas(Math.round((benchmarkType === 'today' ? data.avg_selling_price_today : data.avg_selling_price_week) * data.total_inventory_weight) - data.total_inventory_cost)}
                </h4>
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-[10px] font-bold px-2 py-0.5 bg-emerald-500/20 text-emerald-400 rounded-full border border-emerald-500/30">
                    REALISTIC PROJECTION
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* History Chart */}
          <div className="lg:col-span-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-bold text-slate-800 dark:text-slate-100">Tren Harga Emas (7 Hari Terakhir)</h3>
              <div className="flex gap-2">
                <span className="px-2 py-1 bg-slate-100 dark:bg-slate-800 rounded text-[10px] font-bold text-slate-500">WEEKLY</span>
              </div>
            </div>
            <div className="h-[300px] w-full">
              <Line data={chartData} options={chartOptions} />
            </div>
          </div>

          {/* Profit Simulator Card */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm flex flex-col">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded-lg">
                <IconCalculator size={20} />
              </div>
              <h3 className="font-bold text-slate-800 dark:text-slate-100">Simulasi Keuntungan</h3>
            </div>

            <div className="space-y-6 flex-1">
              <div>
                <label className="block text-sm font-bold text-slate-600 dark:text-slate-400 mb-3">
                  Penyesuaian Harga Jual (per gram)
                </label>
                <div className="flex items-center gap-3">
                  <input 
                    type="range" 
                    min="-50000" 
                    max="50000" 
                    step="1000"
                    value={simulatorAdjustment}
                    onChange={(e) => setSimulatorAdjustment(parseInt(e.target.value))}
                    className="flex-1 accent-amber-500" 
                  />
                  <span className={`w-24 text-right font-bold text-sm ${simulatorAdjustment >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {simulatorAdjustment >= 0 ? '+' : ''} {numberWithCommas(simulatorAdjustment)}
                  </span>
                </div>
                <div className="flex justify-between text-[10px] text-slate-400 font-bold mt-2">
                  <span>- Rp 50k</span>
                  <span>Normal</span>
                  <span>+ Rp 50k</span>
                </div>
              </div>

              <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-dashed border-slate-200 dark:border-slate-700">
                <p className="text-[10px] font-bold text-slate-400 uppercase mb-2">Potensi Tambahan Keuntungan</p>
                <h2 className={`text-2xl font-black ${simulatedProfit >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                  {simulatedProfit >= 0 ? '+' : ''} Rp {numberWithCommas(simulatedProfit)}
                </h2>
                <p className="text-[10px] text-slate-400 mt-2">
                  Jika Anda mengubah harga jual sebesar <span className="text-slate-600 dark:text-slate-300">Rp {numberWithCommas(simulatorAdjustment)}/gr</span> dari harga pasar saat ini.
                </p>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between text-xs">
                  <span className="text-slate-500">Harga Jual Baru:</span>
                  <span className="font-bold text-slate-700 dark:text-slate-200">Rp {numberWithCommas(data.current_gold_price + simulatorAdjustment)}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-500">Total Berat Stok:</span>
                  <span className="font-bold text-slate-700 dark:text-slate-200">{data.total_inventory_weight} gr</span>
                </div>
              </div>
            </div>

            <button 
              onClick={() => setSimulatorAdjustment(0)}
              className="mt-6 w-full py-3 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-xl text-xs font-bold hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
            >
              Reset Simulasi
            </button>
          </div>

        </div>

        {/* Warning Section */}
        {data.price_change_percentage > 2 && (
          <div className="bg-rose-50 dark:bg-rose-900/10 border border-rose-100 dark:border-rose-900/30 p-4 rounded-2xl flex items-start gap-4">
            <div className="p-2 bg-rose-100 dark:bg-rose-900/40 text-rose-600 dark:text-rose-400 rounded-lg shrink-0">
              <IconTrendingUp size={24} />
            </div>
            <div>
              <h4 className="font-bold text-rose-800 dark:text-rose-300">Waspada Perubahan Harga Signifikan!</h4>
              <p className="text-rose-600 dark:text-rose-400/80 text-sm mt-1">
                Harga emas naik lebih dari 2% hari ini. Pertimbangkan untuk menyesuaikan harga jual di sistem agar margin keuntungan Anda tetap terjaga.
              </p>
            </div>
          </div>
        )}

      </div>
    </Layouts>
  );
};

export default GoldValuation;
